/**
 * THE TOUR, CARRIED ACROSS PAGES.
 *
 * A reading order that lives only on one page is a list you read once and
 * forget. This is the other half: once a tour starts, a bar rides along at the
 * bottom of every report saying where you are, what this one answers, and what
 * comes next - so nobody has to hold the sequence in their head or go back to
 * the sidebar to find step four.
 *
 * It is driven entirely by `?tour=<role>` in the URL, which means it survives
 * a refresh, it can be shared as a link ("start here, it walks you through"),
 * and closing it is just leaving the query off. No stored state, nothing to
 * get out of sync with the page you are actually on.
 *
 * It renders nothing at all when no tour is running, which is almost always.
 */
import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';

import { forRole } from '@/lib/report-guide';
import { cn } from '@/lib/utils';

export default function TourBar() {
  const [sp] = useSearchParams();
  const { pathname } = useLocation();
  const role = sp.get('tour');
  if (!role) return null;

  const list = forRole(role);
  // Match on pathname only: a report may carry its own query (a level, a tab)
  // and the tour should still know where it is.
  const i = list.findIndex((r) => r.to.split('?')[0] === pathname);
  if (i < 0) return null;

  const here = list[i];
  const next = list[i + 1] || null;
  const stop = pathname; // leaving the tour is just this page without the param

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
      <div className="pointer-events-auto flex w-full max-w-3xl flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-[hsl(var(--card-border))] bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
        <div className="flex min-w-0 flex-1 basis-[240px] flex-col gap-0.5">
          <p className="text-[13px] font-medium first-letter:uppercase text-muted-foreground">
            Step {i + 1} of {list.length} &middot; {here.title}
          </p>
          <p className="truncate text-[13.5px] text-foreground" title={here.asks}>{here.asks}</p>
        </div>
        {next ? (
          <Link to={`${next.to}${next.to.includes('?') ? '&' : '?'}tour=${role}`}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition-transform hover:-translate-y-px">
            {next.title} <ArrowRight className="size-3.5" />
          </Link>
        ) : (
          <Link to="/growth/guide"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground">
            That is the tour
          </Link>
        )}
        <Link to={stop} replace aria-label="Leave the tour"
          className={cn('shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground')}>
          <X className="size-4" />
        </Link>
      </div>
    </div>
  );
}
